import {
  required,
  email,
  minLength,
  maxLength,
  helpers,
} from "vuelidate/lib/validators";

// Map of exported validation functions to their names
// This allows us to properly name validators when used in generateValidations
const validatorNameMap = new WeakMap();

// Register validator functions with their names
const registerValidator = (validator, name) => {
  validatorNameMap.set(validator, name);
  return validator;
};

// Custom validation: Only letters and spaces
export const onlyLetters = registerValidator(
  (value) => /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value),
  'onlyLetters'
);

// Custom validation: No special characters
export const noSpecialChars = registerValidator(
  (value) => /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]*$/.test(value),
  'noSpecialChars'
);

// Custom validation: No spaces
export const noSpaces = registerValidator(
  (value) => !/\s/.test(value),
  'noSpaces'
);

// Custom validation: Detects if contains commas or apostrophes (incorrect separators)
export const noCommasOrApostrophes = registerValidator(
  (value) => /^[^,']*$/.test(value),
  'noCommasOrApostrophes'
);

// Custom validation: Only numeric characters and dots
export const noInvalidCharacters = registerValidator(
  (value) => /^[0-9.]*$/.test(value),
  'noInvalidCharacters'
);

// Custom validation: Integer or decimal number with dot
export const validDecimal = registerValidator(
  (value) => /^-?\d*(\.\d+)?$/.test(value),
  'validDecimal'
);

// Custom validation: Only numeric
export const onlyNumeric = registerValidator(
  (value) => /^-?\d+$/.test(value),
  'onlyNumeric'
);

export const validDateFormat = helpers.withParams(
  { type: "validDateFormat" },
  (value) => {
    if (!value) return false; // Permitir vacío
    const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
    return regex.test(value);
  }
);

export const validEndDate = helpers.withParams(
  { type: "validEndDate" },
  (value, parentVm) => {
    // `parentVm` es el contexto del elemento actual (action)
    if (!value || !parentVm.init_date) return true; // Permitir vacío o si no hay `init_date`
    const startDate = new Date(parentVm.init_date);
    const endDate = new Date(value);
    return endDate >= startDate; // `end_date` debe ser mayor o igual que `init_date`
  }
);

// Helper function to get validator name
const getValidatorName = (validator, index) => {
  // Check if validator is registered in our map
  if (validatorNameMap.has(validator)) {
    return validatorNameMap.get(validator);
  }
  
  // If validator has $params.type (vuelidate helpers.withParams), use it
  if (validator?.$params?.type) {
    return validator.$params.type;
  }
  
  // Try to extract name from function name (works for named functions)
  if (validator?.name && validator.name !== 'anonymous' && validator.name !== '') {
    // Convert camelCase to camelCase (keep as is)
    return validator.name;
  }
  
  // For vuelidate validators with params, try to get type
  if (validator?.$params) {
    const params = Object.keys(validator.$params);
    if (params.length > 0) {
      return params[0];
    }
  }
  
  // Fallback: use index-based name
  return `custom${index}`;
};

// Generador de validaciones
export const generateValidations = (fields, customRules = {}) => {
  const validations = {};

  fields.forEach((field) => {
    const baseRules = [{ name: "required", rule: required }];
    const fieldSpecificRules = [];

    // Reglas específicas para `email`
    if (field === "email") {
      fieldSpecificRules.push({ name: "email", rule: email });
    }

    // Reglas específicas para `password`
    if (field === "password") {
      fieldSpecificRules.push(
        { name: "minLength", rule: minLength(8) },
        { name: "maxLength", rule: maxLength(20) }
      );
    }

    // Combina las reglas predeterminadas con personalizadas
    const customFieldRules = customRules[field] || [];
    const customRulesWithNames = customFieldRules.map((rule, index) => ({
      name: getValidatorName(rule, index),
      rule,
    }));

    const combinedRules = [...baseRules, ...fieldSpecificRules, ...customRulesWithNames];

    // Asignar reglas al campo
    validations[field] = combinedRules.reduce((acc, { name, rule }) => {
      acc[name] = rule;
      return acc;
    }, {});
  });

  return validations;
};
